import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [fazenda, setFazenda] = useState(null);
  const [permissoes, setPermissoes] = useState(null);
  const [cargo, setCargo] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const fazendaData = localStorage.getItem('fazenda');
    const permissoesData = localStorage.getItem('permissoes');
    const cargoData = localStorage.getItem('cargo');
    const mustChange = localStorage.getItem('must_change_password') === 'true';

    if (token && userData) {
      try {
        setToken(token);
        setUser(JSON.parse(userData));
        if (fazendaData) setFazenda(JSON.parse(fazendaData));
        if (permissoesData) setPermissoes(JSON.parse(permissoesData));
        if (cargoData) setCargo(cargoData);
        setMustChangePassword(mustChange);
      } catch {
        // dados corrompidos: limpa tudo
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback((userData, token, fazendaData, permissoesData, cargoData, refreshToken, mustChangePwd = false) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('fazenda', JSON.stringify(fazendaData));
    localStorage.setItem('permissoes', JSON.stringify(permissoesData));
    localStorage.setItem('cargo', cargoData);
    localStorage.setItem('must_change_password', mustChangePwd ? 'true' : 'false');
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
    setToken(token);
    setUser(userData);
    setFazenda(fazendaData);
    setPermissoes(permissoesData);
    setCargo(cargoData);
    setMustChangePassword(mustChangePwd);
  }, []);

  const clearMustChangePassword = useCallback(() => {
    localStorage.setItem('must_change_password', 'false');
    setMustChangePassword(false);
  }, []);

  const logout = useCallback(() => {
    ['token', 'user', 'fazenda', 'permissoes', 'cargo', 'refresh_token', 'pre_token', 'fazendas_disponiveis', 'pre_user', 'must_change_password'].forEach(
      (k) => localStorage.removeItem(k)
    );
    setToken(null);
    setUser(null);
    setFazenda(null);
    setPermissoes(null);
    setCargo(null);
    setMustChangePassword(false);
  }, []);

  // `tela` é a chave granular por tela (ex.: 'financeiro.clientes'), não
  // mais o módulo inteiro (ex.: 'financeiro') — cada tela tem suas próprias
  // 4 ações: leitura, gravacao (criar), edicao (editar), exclusao.
  const temPermissao = useCallback(
    (tela, acao = 'leitura') => {
      if (!permissoes) return false;
      return permissoes[tela]?.[acao] ?? false;
    },
    [permissoes]
  );

  const podeGravar = useCallback((tela) => temPermissao(tela, 'gravacao'), [temPermissao]);
  const podeEditar = useCallback((tela) => temPermissao(tela, 'edicao'), [temPermissao]);
  const podeEscrever = useCallback((tela) => podeGravar(tela) || podeEditar(tela), [podeGravar, podeEditar]);
  const podeExcluir = useCallback((tela) => temPermissao(tela, 'exclusao'), [temPermissao]);

  const isGestor = cargo && ['PROPRIETARIO', 'DIRETOR', 'GESTOR'].includes(cargo);
  const isDiretor = cargo && ['PROPRIETARIO', 'DIRETOR'].includes(cargo);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        fazenda,
        permissoes,
        cargo,
        mustChangePassword,
        loading,
        login,
        logout,
        clearMustChangePassword,
        temPermissao,
        podeGravar,
        podeEditar,
        podeEscrever,
        podeExcluir,
        isGestor,
        isDiretor,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
};
